import { useEffect, useRef } from 'react';
import { ListChecks, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { AnswersToCheckPanel } from './AnswersToCheckPanel';
import { ReviewQueuePanel } from './ReviewQueuePanel';
import { DecisionApprovalsPage } from './DecisionApprovalsPage';
import { EscalationInbox } from './EscalationInbox';
import { TodoList } from './TodoList';
import type { UseQueueResult } from '../hooks/useQueue';
import { QUEUE_COUNTING, queueBreakdown } from '../utils/queueCounts';

/**
 * QueueTab — the one place a consultant looks for what needs them.
 *
 * One headline with one count, then the sections in a fixed order: answers
 * to check, client submissions and items for review and action items (the
 * review queue panel renders those three), decisions waiting on approval,
 * escalations, and the person's own to-dos. Each section is the component that already served it;
 * what changed is that they live together and their counts add up to the
 * number on the tab and on the "Needs Your Action" card (ledger L22).
 */

interface QueueTabProps {
  accessToken: string;
  currentUser: any;
  queue: UseQueueResult;
  /** The dashboard's action items, preloaded, for the review queue panel. */
  actionItemsData: any;
}

export function QueueTab({ accessToken, currentUser, queue, actionItemsData }: QueueTabProps) {
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const { answers, counts, sections, total, headline, loading } = queue;

  // A section chip scrolls to its section.
  const jump = (id: string) => sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  useEffect(() => {
    document.title = !loading && total > 0 ? `(${total}) Queue · PathKit` : 'Queue · PathKit';
    return () => { document.title = 'PathKit'; };
  }, [total, loading]);

  return (
    <div className="space-y-6" data-testid="queue-tab">
      <header className="flex flex-col gap-3 rounded-lg border bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <ListChecks className="size-5 text-orange-600" aria-hidden="true" />
          <div>
            <h2 data-testid="queue-headline" aria-live="polite">{loading ? QUEUE_COUNTING : headline}</h2>
            <p className="text-muted-foreground">
              {loading ? 'Answers, submissions, reviews, action items, decisions, escalations and to-dos are on their way.' : (queueBreakdown(sections) || 'Answers, submissions, reviews, action items, decisions, escalations and to-dos all land here.')}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!loading && sections.filter((s) => s.count > 0).map((s) => (
            <Button key={s.id} variant="outline" size="sm" onClick={() => jump(s.id)}>
              {s.label} <span className="ml-1 rounded-full bg-orange-50 px-1.5 text-orange-700">{s.count}</span>
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={queue.refresh} title="Reload every section">
            <RefreshCw className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </header>

      <section ref={(el) => { sectionRefs.current.answers = el; }} aria-label="Answers to check">
        <AnswersToCheckPanel accessToken={accessToken} preloaded={answers} />
      </section>

      <section ref={(el) => { sectionRefs.current.review = el; sectionRefs.current.submissions = el; sectionRefs.current.actions = el; }} aria-label="Submissions, reviews and action items">
        <ReviewQueuePanel accessToken={accessToken} currentUser={currentUser} actionItemsData={actionItemsData} />
      </section>

      {counts.decisions > 0 && (
        <section ref={(el) => { sectionRefs.current.decisions = el; }} aria-label="Decisions">
          <DecisionApprovalsPage accessToken={accessToken} currentUser={currentUser} />
        </section>
      )}

      {counts.escalations > 0 && (
        <section ref={(el) => { sectionRefs.current.escalations = el; }} aria-label="Escalations">
          <h3 className="mb-3">Escalations</h3>
          <EscalationInbox accessToken={accessToken} currentUser={currentUser} compact />
        </section>
      )}

      <section ref={(el) => { sectionRefs.current.todos = el; }} aria-label="To-dos">
        <TodoList todos={queue.todos} />
      </section>
    </div>
  );
}
